import { ColDef, ICellRendererParams } from 'ag-grid-community'
import React from 'react'
import { GridDataCellTypes, IModalObject } from '.'
import { ThumbnailCellRenderer } from './GridData'
import DateAndTime from '../../features/DateAndTime'
import Countries from '../../features/locations/Countrries'
import StatusChip from '../StatusChip'
import GridDataCellWrapper from './GridDataCellWrapper'
import { Avatar } from '@mui/material'
import GenerateIcon from '../GenerateIcon'
import BaseApp, { replaceItemDataInLink } from '../../features/BaseApp'
import { useDispatch } from 'react-redux'
import { handleCloseElementQuickPreview, handleOpenElementQuickPreview } from '../../features/admin/adminPagesSlice'
import { useNavigate } from 'react-router-dom'
import { useReturnCurrentUser } from '../../features/user/userSlice'

interface IProps extends ICellRendererParams<IModalObject> {
    type: GridDataCellTypes,
    appClass?: () => BaseApp,
    enableQuickPreview?: boolean,
    onClick?: (data: IModalObject | undefined) => void,
    additionalContent?: (data: IModalObject | undefined) => JSX.Element,
    elemIdTitle?: string,
    customViewLink?: string
}

export function GridDataTitleCell(props: IProps){

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { 
        data, 
        value, 
        appClass,
        enableQuickPreview,
        onClick,
        additionalContent,
        customViewLink,
    } = props

    const instance = typeof appClass === "function" ? appClass() : appClass

    const currentUser = useReturnCurrentUser()

    let link: string = ""

    if (instance && instance.gridConfig){
        if (data){
            link = replaceItemDataInLink(customViewLink ? customViewLink : instance.gridConfig.viewItemLink, data)

            if (!currentUser.is_superuser || !currentUser.is_daemon){
                if (instance.gridConfig.publicViewItemLink){
                    link = replaceItemDataInLink(customViewLink ? customViewLink : instance.gridConfig.publicViewItemLink, data)
                }
            }
        }
    }
    

    const handleOnClick = () => {

        if (enableQuickPreview){
            handleOpenElementQuickPreview(
                data && data.id ? data.id : 0,
                value,
                appClass ? appClass : () => new BaseApp,
                dispatch,
                [
                     {
                        color: "success",
                        label: "Open",
                        onClick: () => {
                            handleCloseElementQuickPreview(dispatch)
                            navigate(link)
                        },
                        disabled: link ? false : true
                    },
                    {
                        color: "error",
                        label: "Close",
                        onClick: () => handleCloseElementQuickPreview(dispatch)
                    }
                ],
                false,
                undefined,
                true
            )
        }

        if (onClick){
            onClick(data)
        }

        return
    }

    return(
        <GridDataCellWrapper>
            <div className="grid-data-cell-title-box" onClick={() => handleOnClick()}>

                <Avatar className="thumbnail-of-grid-data-cell-title-box" src={data && data.thumbnail ? data.thumbnail : ""}>
                    <GenerateIcon icon="image" />
                </Avatar>

                <div>
                    <h3 className="title">{value}</h3>
                    {additionalContent && additionalContent(data)}
                </div>

            </div>
        </GridDataCellWrapper>
    )
}

export function GridDataQuickPreviewCell(props: IProps){

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { 
        data, 
        value, 
        appClass,
        elemIdTitle,
    } = props

    const instance = typeof appClass === "function" ? appClass() : appClass

    let link: string = ""

    if (instance && instance.gridConfig){
        if (data){
            link = replaceItemDataInLink(instance.gridConfig.viewItemLink, data)
        }
    }

    let title: string = value

    if (!title){
        if (data && data.title){
            title = data.title
        }
    }

    let id: number = 0

    if (elemIdTitle){
        if (data){
            if (data[elemIdTitle]){
                id = data[elemIdTitle]
            }
        }
    }

    const handleOnClick = () => {

        const appClassInstance = appClass ? appClass : () => new BaseApp

        handleOpenElementQuickPreview(
                id,
                title,
                appClassInstance,
                dispatch,
                [
                     {
                        color: "success",
                        label: "Open",
                        onClick: () => {
                            handleCloseElementQuickPreview(dispatch)
                            navigate(link)
                        },
                        disabled: link ? false : true
                    },
                    {
                        color: "error",
                        label: "Close",
                        onClick: () => handleCloseElementQuickPreview(dispatch)
                    }
                ],
                false,
                undefined,
                true
        )

    }

    return(
        <GridDataCellWrapper>
            <div className="quick-preview-title-box">
                {value ? <h3 className="title" onClick={() => handleOnClick()} style={{ cursor: "pointer" }}>{value}</h3> : <>-</>}
            </div>
        </GridDataCellWrapper>
    )
}

export default function GridDataCell(props: IProps) {

    const { data, value, type } = props

    const dateAndTime = new DateAndTime()

    const tryToFormatDate = (val: any) => {
        if (!val){
            return "-"
        }
        
        if (typeof val !== "string"){
            return "Invalid date"
        }

        try{
            return dateAndTime.formatDateAndTime(val)
        }catch (error){
            console.log(error)
            return "Invalid date"
        }
    }

    switch(type){

        case "thumbnail":
            return <ThumbnailCellRenderer {...props} />
            
        case "date":
            return tryToFormatDate(value)

        case "country":
            const country = Countries.returnCountryBasedByValue(value)

            if (!country){
                return <p>?</p>
            }

            return `${country.flag} ${country.name}`

        case "status":
            return(
                <GridDataCellWrapper>
                    <StatusChip type={value} />
                </GridDataCellWrapper>
            ) 

        case "title":
            return <GridDataTitleCell {...props} />

        case "quick-preview":
            return <GridDataQuickPreviewCell {...props} />

    }

    return value ? value : "-"
}